// Pure, dependency-free scoring for the IGNTR sales-profile questionnaire.
// All business rules are exported consts so they can be adjusted in one place.

export interface SalesProfileAnswer {
  q: number;
  options: string[];
  tags: (string | undefined)[];
}

export interface SalesProfileAnswers {
  answers: SalesProfileAnswer[];
}

export type SalesProfileTrack = 'JAGT' | 'FASTHOLD' | 'UDVID';

export interface SalesProfileScore {
  track: SalesProfileTrack;
  trackLabel: string;
  focusAreas: string[];
  motivations: string[];
  freeText: string;
}

export const FOCUS_AREA_COUNT = 2;

// Track resolved from Q1 tags: whichever tag has the strict max count wins.
// On ANY tie for the max we default to this track (per the concept doc).
export const TRACK_TIE_BREAK_DEFAULT: SalesProfileTrack = 'UDVID';

const TRACKS: SalesProfileTrack[] = ['JAGT', 'FASTHOLD', 'UDVID'];

// Danish display labels, taken verbatim from the questionnaire step sub-text
// 'Jagt · Fasthold · Udvid'.
export const TRACK_LABELS: Record<SalesProfileTrack, string> = {
  JAGT: 'Jagt',
  FASTHOLD: 'Fasthold',
  UDVID: 'Udvid',
};

function tagsForQuestion(answers: SalesProfileAnswers, q: number): string[] {
  return answers.answers
    .filter((a) => a.q === q)
    .flatMap((a) => a.tags)
    .filter((t): t is string => !!t);
}

function resolveTrack(answers: SalesProfileAnswers): SalesProfileTrack {
  const counts: Record<SalesProfileTrack, number> = {
    JAGT: 0,
    FASTHOLD: 0,
    UDVID: 0,
  };
  for (const tag of tagsForQuestion(answers, 1)) {
    if (tag in counts) {
      counts[tag as SalesProfileTrack]++;
    }
  }
  const max = Math.max(...TRACKS.map((t) => counts[t]));
  const leaders = TRACKS.filter((t) => counts[t] === max);
  // A single strict-max tag wins; any tie (including all-zero) falls back.
  return leaders.length === 1 ? leaders[0] : TRACK_TIE_BREAK_DEFAULT;
}

function resolveFocusAreas(answers: SalesProfileAnswers): string[] {
  // Stable first-appearance order across Q2..Q5 so ties are deterministic.
  const order: string[] = [];
  const counts = new Map<string, number>();
  for (const q of [2, 3, 4, 5]) {
    for (const tag of tagsForQuestion(answers, q)) {
      if (!counts.has(tag)) {
        counts.set(tag, 0);
        order.push(tag);
      }
      counts.set(tag, (counts.get(tag) ?? 0) + 1);
    }
  }
  return order
    .map((tag, index) => ({ tag, count: counts.get(tag) ?? 0, index }))
    .sort((a, b) => b.count - a.count || a.index - b.index)
    .slice(0, FOCUS_AREA_COUNT)
    .map((x) => x.tag);
}

export function scoreSalesProfile(
  answers: SalesProfileAnswers,
  freeText: string,
): SalesProfileScore {
  const track = resolveTrack(answers);
  return {
    track,
    trackLabel: TRACK_LABELS[track],
    focusAreas: resolveFocusAreas(answers),
    motivations: tagsForQuestion(answers, 6),
    freeText: (freeText ?? '').trim(),
  };
}
